import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { WhatsAppCTA } from '../components/ui/whatsapp-cta';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { API_BASE } from '../config/api';
import { renderTipTapToHTML } from '../utils/tiptapRenderer';

export const ScholarshipDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [scholarship, setScholarship] = useState(null);
  const [relatedPrograms, setRelatedPrograms] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = API_BASE;

  useEffect(() => {
    const fetchScholarshipData = async () => {
      try {
        setLoading(true);
        
        const scholarshipResponse = await fetch(`${backendUrl}/api/public/scholarships/${slug}`);
        if (!scholarshipResponse.ok) {
          if (scholarshipResponse.status === 404) {
            throw new Error('Scholarship not found');
          }
          throw new Error('Failed to fetch scholarship details');
        }
        const scholarshipData = await scholarshipResponse.json();
        
        // Fetch all programs to find related ones
        const allProgramsResponse = await fetch(`${backendUrl}/api/public/programs`);
        const universitiesResponse = await fetch(`${backendUrl}/api/public/universities`);
        
        if (allProgramsResponse.ok && universitiesResponse.ok) {
          const allProgramsData = await allProgramsResponse.json();
          const universitiesData = await universitiesResponse.json();
          
          // Create university lookup map
          const universityMap = {};
          (universitiesData.universities || []).forEach(uni => {
            universityMap[uni.id] = uni.name;
          });
          
          // Filter programs by scholarship's field_of_study or subject
          const scholarshipSubjects = scholarshipData.scholarship.applicable_subjects || [];
          const scholarshipField = scholarshipData.scholarship.field_of_study;
          
          const related = (allProgramsData.programs || [])
            .filter(p => {
              // Match by field_of_study or subject
              if (scholarshipField && p.field_of_study_primary === scholarshipField) return true;
              if (scholarshipSubjects.length > 0 && p.subject && scholarshipSubjects.includes(p.subject)) return true;
              return false;
            })
            .slice(0, 8)
            .map(p => ({
              ...p,
              university_name: universityMap[p.university_id] || 'Unknown University'
            }));
          
          setAllPrograms(related);
        }
        
        // Fetch programs from the same university (fallback)
        if (scholarshipData.scholarship.university_id) {
          const programsResponse = await fetch(`${backendUrl}/api/public/programs?university_id=${scholarshipData.scholarship.university_id}`);
          if (programsResponse.ok) {
            const programsData = await programsResponse.json();
            setRelatedPrograms((programsData.programs || []).slice(0, 6));
          }
        }
        
        setScholarship(scholarshipData.scholarship);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchScholarshipData();
    }
  }, [id, backendUrl]);

  const handleConsultClick = () => {
    window.open('https://wa.me/60123456789?text=Hi, I would like to inquire about this scholarship', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-[#4B5563]">Loading scholarship details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#111827] mb-4">Scholarship Not Found</h1>
            <p className="text-[#4B5563] mb-6">{error}</p>
            <button 
              onClick={() => navigate('/scholarships')}
              className="px-6 py-3 bg-[#F97316] hover:bg-[#fb7e2e] text-white font-semibold rounded-lg transition-colors"
            >
              Browse All Scholarships
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!scholarship) return null;

  // Extract text from TipTap JSON for description
  const extractTextFromTipTap = (tiptapJson) => {
    if (!tiptapJson || typeof tiptapJson !== 'object') return '';
    if (tiptapJson.type === 'doc' && Array.isArray(tiptapJson.content)) {
      const extract = (node) => {
        if (node.type === 'text') return node.text || '';
        if (node.content && Array.isArray(node.content)) {
          return node.content.map(extract).join(' ');
        }
        return '';
      };
      return tiptapJson.content.map(extract).join(' ').trim();
    }
    return '';
  };

  // Prepare meta tags for scholarship
  const scholTitle = scholarship.seo_title || scholarship.name || 'Scholarship';
  const scholDescription = truncateText(
    scholarship.seo_description || 
    extractTextFromTipTap(scholarship.about) || 
    extractTextFromTipTap(scholarship.description) ||
    `${scholarship.name || 'Scholarship'}${scholarship.provider ? ` by ${scholarship.provider}` : ''}. ${scholarship.value_type === 'full' ? 'Full scholarship' : scholarship.value_amount || ''}`,
    200
  );
  const scholImage = ''; // Scholarships don't have images in current schema
  const scholUrl = `https://www.unisave.my/scholarships/${scholarship.slug || scholarship.id}`;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col overflow-x-hidden">
      {/* Open Graph & Twitter Card Meta for Scholarship */}
      <Helmet>
        <title>{scholTitle} | Unisave</title>
        <meta name="description" content={scholDescription} />
        <link rel="canonical" href={scholUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={scholTitle} />
        <meta property="og:description" content={scholDescription} />
        <meta property="og:image" content={ensureAbsoluteUrl(scholImage)} />
        <meta property="og:url" content={scholUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Unisave" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={scholTitle} />
        <meta name="twitter:description" content={scholDescription} />
        <meta name="twitter:image" content={ensureAbsoluteUrl(scholImage)} />
      </Helmet>
      
      <Header />
      
      <main className="flex-1 w-full max-w-full px-4 md:px-12 lg:px-20">
        {/* BREADCRUMB */}
        <div className="max-w-[1280px] mx-auto pt-4">
          <Breadcrumb items={[
            { label: 'Scholarships', href: '/scholarships' },
            { label: scholarship.name || scholarship.scholarship_name, href: '#' }
          ]} />
        </div>

        {/* SECTION 1: HERO */}
        <section className="py-6 md:py-12 bg-white rounded-2xl mt-4 md:mt-6 shadow-sm w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-0 md:px-6 lg:px-10 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 w-full">
              {/* Left: Scholarship Info */}
              <div className="lg:col-span-2 px-4 md:px-0 w-full max-w-full">
                <h1 className="text-3xl md:text-4xl font-bold text-[#111827] mb-4 break-words">
                  {scholarship.name}
                </h1>
                
                <p className="text-lg text-[#6B7280] font-medium mb-6 break-words">
                  {scholarship.provider || 'University Scholarship'}
                </p>
                
                <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FDE68A] rounded-xl p-6 mb-6">
                  <p className="text-sm text-[#92400E] font-medium mb-2">Scholarship Value</p>
                  <p className="text-3xl font-bold text-[#92400E]">
                    {scholarship.value_amount ? (
                      !isNaN(Number(scholarship.value_amount)) 
                        ? `RM ${Number(scholarship.value_amount).toLocaleString()}`
                        : scholarship.value_amount
                    ) : (scholarship.coverage || 'Value varies')}
                  </p>
                </div>
              </div>
              
              {/* Right: Sticky CTA Card */}
              <div className="lg:col-span-1 px-4 md:px-0 w-full max-w-full">
                <div className="bg-gradient-to-br from-white to-[#F8FAFC] border-2 border-[#E5E7EB] rounded-2xl p-6 lg:sticky lg:top-24 shadow-xl">
                  <h3 className="text-lg font-bold text-[#111827] mb-4">Interested in this scholarship?</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleConsultClick}
                      className="w-full px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all"
                    >
                      Consult Us
                    </button>
                    <button
                      onClick={handleConsultClick}
                      className="w-full px-6 py-3 bg-white border-2 border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Chat on WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: KEY HIGHLIGHTS BAR */}
        <section className="py-6 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full">
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-4">
                <div className="w-12 h-12 bg-[#FEF3C7] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#92400E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Value</p>
                  <p className="text-sm font-bold text-[#111827] break-words">
                    {scholarship.value_amount ? (
                      !isNaN(Number(scholarship.value_amount)) 
                        ? `RM ${Number(scholarship.value_amount).toLocaleString()}`
                        : scholarship.value_amount
                    ) : 'Varies'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-4">
                <div className="w-12 h-12 bg-[#E0F2FE] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#0369A1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Level</p>
                  <p className="text-sm font-bold text-[#111827] break-words">
                    {scholarship.eligible_levels ? scholarship.eligible_levels.join(', ') : 'All Levels'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-4">
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#DC2626]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Deadline</p>
                  <p className="text-sm font-bold text-[#111827] break-words">
                    {scholarship.application_close ? new Date(scholarship.application_close).toLocaleDateString() : 
                     (scholarship.availability === 'All Year' ? 'Open All Year' : 'All Year')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 bg-[#F8FAFC] rounded-lg p-4">
                <div className="w-12 h-12 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] font-medium">Provider</p>
                  <p className="text-sm font-bold text-[#111827] break-words">
                    {scholarship.provider_type || 'University'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3: ABOUT THE SCHOLARSHIP */}
        <section className="py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">About the Scholarship</h2>
            <div className="prose prose-lg max-w-none">
              {scholarship.about && Object.keys(scholarship.about).length > 0 ? (
                <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(scholarship.about) }} />
              ) : (
                <p className="text-[#4B5563] leading-relaxed">
                  This scholarship provides financial support to deserving students pursuing their academic goals. 
                  For detailed information and application requirements, please consult with our advisors.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 4: ELIGIBILITY CRITERIA */}
        <section className="py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">Eligibility Criteria</h2>
            <div className="prose prose-lg max-w-none">
              {scholarship.eligibility && Object.keys(scholarship.eligibility).length > 0 ? (
                <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(scholarship.eligibility) }} />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[#4B5563]">Academic merit and financial need assessment</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[#4B5563]">Meeting the university's admission requirements</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#059669] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[#4B5563]">Consult with our advisors for complete eligibility criteria</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 5: APPLICABLE UNIVERSITIES / PROGRAMS */}
        {relatedPrograms.length > 0 && (
          <section className="py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
              <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">Applicable Programs</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedPrograms.slice(0, 6).map(program => (
                  <div
                    key={program.id}
                    onClick={() => navigate(`/programs/${program.slug || program.id}`)}
                    className="bg-white border-2 border-[#E5E7EB] rounded-xl p-4 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer"
                  >
                    <h3 className="font-bold text-[#111827] mb-2 line-clamp-2 text-sm">
                      {program.program_name}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-[#E0F2FE] text-[#0369A1] text-xs font-semibold rounded">
                        {program.level_of_study}
                      </span>
                    </div>
                    {program.estimated_fee_local && (
                      <p className="text-sm font-bold text-[#111827]">
                        RM {program.estimated_fee_local.toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              
              {relatedPrograms.length > 6 && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => navigate('/programs')}
                    className="px-6 py-3 bg-white border-2 border-[#E5E7EB] text-[#111827] hover:border-[#1E40AF] hover:text-[#1E40AF] font-semibold rounded-lg transition-all"
                  >
                    View All Programs
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* SECTION 6: HOW TO APPLY */}
        <section className="py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
            <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">How to Apply</h2>
            <div className="prose prose-lg max-w-none">
              {scholarship.how_to_apply && Object.keys(scholarship.how_to_apply).length > 0 ? (
                <div dangerouslySetInnerHTML={{ __html: renderTipTapToHTML(scholarship.how_to_apply) }} />
              ) : (
                <div className="bg-gradient-to-br from-[#F8FAFC] to-white border-2 border-[#E5E7EB] rounded-xl p-6">
                  <p className="text-[#4B5563] mb-6 leading-relaxed">
                    Our education advisors will guide you through the complete scholarship application process, 
                    ensuring you meet all requirements and submit the necessary documentation on time.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-[#1E40AF] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        1
                      </div>
                      <div>
                        <h4 className="font-bold text-[#111827] mb-1">Consult with Our Advisors</h4>
                        <p className="text-sm text-[#6B7280]">Discuss your eligibility and gather required documents</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-[#1E40AF] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        2
                      </div>
                      <div>
                        <h4 className="font-bold text-[#111827] mb-1">Prepare Application</h4>
                        <p className="text-sm text-[#6B7280]">We help you compile and review all necessary documents</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-[#1E40AF] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                        3
                      </div>
                      <div>
                        <h4 className="font-bold text-[#111827] mb-1">Submit Application</h4>
                        <p className="text-sm text-[#6B7280]">Our team ensures timely submission to the university or organization</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {scholarship.cta_link && (
              <a
                href={scholarship.cta_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-block px-8 py-3 bg-gradient-to-r from-[#F97316] to-[#ea580c] hover:shadow-xl text-white font-semibold rounded-lg transition-all"
              >
                {scholarship.cta_text || 'Apply Now'}
              </a>
            )}
            <button
              onClick={handleConsultClick}
              className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e40afdd] hover:shadow-xl text-white font-semibold rounded-lg transition-all"
            >
              Start Your Application
            </button>
          </div>
        </section>

        {/* SECTION 7: RELATED PROGRAMS */}
        {allPrograms.length > 0 && (
          <section className="py-10 bg-white rounded-2xl mt-4 shadow-sm w-full max-w-full overflow-x-hidden">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
              <h2 className="text-2xl md:text-3xl font-bold text-[#111827] mb-6">Related Programs</h2>
              
              {/* Desktop: 4-column grid */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {allPrograms.slice(0, 4).map((program) => (
                  <div
                    key={program.id}
                    onClick={() => navigate(`/programs/${program.slug || program.id}`)}
                    className="bg-white border-2 border-[#E5E7EB] rounded-xl p-2.5 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col h-full"
                  >
                    <h3 className="text-[#111827] mb-0.5 line-clamp-2 hover:text-[#F97316] transition-colors leading-tight" style={{ fontSize: '1.2rem', fontWeight: 650, minHeight: '2.5rem' }}>
                      {program.program_name}
                    </h3>
                    <p className="text-[#4B5563] mb-1.5 font-semibold" style={{ fontSize: '0.80rem' }}>{program.university_name}</p>
                    
                    <div className="flex flex-wrap gap-1 mb-2">
                      <span className="px-1.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
                        {program.level_of_study}
                      </span>
                      <span className="px-1.5 py-0.5 bg-[#F8FAFC] text-[#4B5563] rounded border border-[#E5E7EB]" style={{ fontSize: '0.65rem' }}>
                        {program.mode_of_study}
                      </span>
                    </div>
                    
                    <div className="space-y-1.5 mb-2">
                      {program.estimated_fee_local && (
                        <div className="pt-1.5 border-t border-[#E5E7EB]">
                          <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>Local Student Fee</p>
                          <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                            RM {program.estimated_fee_local.toLocaleString()}
                          </p>
                        </div>
                      )}
                      
                      {program.estimated_fee_international && (
                        <div className="pt-1">
                          <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>International Student Fee</p>
                          <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                            RM {program.estimated_fee_international.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      className="w-full mt-auto px-2.5 py-1.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all"
                      style={{ fontSize: '0.7rem' }}
                    >
                      Explore Program →
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Mobile: Horizontal Scrollable Carousel */}
              <div className="md:hidden relative w-full max-w-full overflow-x-hidden">
                <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory w-full">
                  {allPrograms.slice(0, 8).map((program) => (
                    <div
                      key={program.id}
                      onClick={() => navigate(`/programs/${program.slug || program.id}`)}
                      className="bg-white border-2 border-[#E5E7EB] rounded-xl p-2.5 hover:border-[#F97316] hover:shadow-lg transition-all cursor-pointer flex flex-col min-w-[280px] flex-shrink-0 snap-center"
                    >
                      <h3 className="text-[#111827] mb-0.5 line-clamp-2 hover:text-[#F97316] transition-colors leading-tight" style={{ fontSize: '1rem', fontWeight: 650, minHeight: '2.5rem' }}>
                        {program.program_name}
                      </h3>
                      <p className="text-[#4B5563] mb-1.5 font-semibold" style={{ fontSize: '0.80rem' }}>{program.university_name}</p>
                      
                      <div className="flex flex-wrap gap-1 mb-2">
                        <span className="px-1.5 py-0.5 bg-[#E0F2FE] text-[#0369A1] font-semibold rounded" style={{ fontSize: '0.65rem' }}>
                          {program.level_of_study}
                        </span>
                        <span className="px-1.5 py-0.5 bg-[#F8FAFC] text-[#4B5563] rounded border border-[#E5E7EB]" style={{ fontSize: '0.65rem' }}>
                          {program.mode_of_study}
                        </span>
                      </div>
                      
                      <div className="space-y-1.5 mb-2">
                        {program.estimated_fee_local && (
                          <div className="pt-1.5 border-t border-[#E5E7EB]">
                            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>Local Student Fee</p>
                            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                              RM {program.estimated_fee_local.toLocaleString()}
                            </p>
                          </div>
                        )}
                        
                        {program.estimated_fee_international && (
                          <div className="pt-1">
                            <p className="text-[#6B7280] mb-0.5" style={{ fontSize: '0.65rem' }}>International Student Fee</p>
                            <p className="font-bold text-[#111827]" style={{ fontSize: '0.8rem' }}>
                              RM {program.estimated_fee_international.toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <button
                        className="w-full mt-auto px-2.5 py-1.5 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-lg text-white font-semibold rounded-lg transition-all"
                        style={{ fontSize: '0.7rem' }}
                      >
                        Explore Program →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 8: FINAL ADVISORY CTA */}
        <section className="py-12 bg-white rounded-2xl mt-4 mb-6 shadow-sm border border-[#E5E7EB] w-full max-w-full overflow-x-hidden">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-10 w-full">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-3">Need Help with Your Application?</h2>
              <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
                Our advisors are ready to assist you in securing this scholarship
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
              <button
                onClick={handleConsultClick}
                className="px-6 py-3 bg-gradient-to-r from-[#F97316] to-[#fb7e2e] hover:shadow-xl text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                Consult Now
              </button>
              <button
                onClick={handleConsultClick}
                className="px-6 py-3 bg-white border-2 border-[#059669] text-[#059669] hover:bg-[#059669] hover:text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppCTA phone="60123456789" message={`Hi, I would like to inquire about ${scholarship.name}`} />
    </div>
  );
};

export default ScholarshipDetail;
